import {
  CategoryFilterEnum,
  SpendCategory,
  SpendTransactionFix,
  useSpendBudgetCreateMutation,
  useSpendCategoriesLazyQuery,
} from "graphql/generated";
import { getDatePickerValue, setDatePickerValue } from "helpers/date-picker";
import { FormatDate } from "helpers/format-date";
import { FormatMoney, ParseMoney } from "helpers/format-money";
import { isNullOrEmpty } from "helpers/null-or-empty";
import { useAppContexts } from "hooks/use-context";
import { useEffect, useState } from "react";
import DatePicker from "shared-components/date-picker";
import InputMask from "shared-components/input-mask";
import { SnapButton, SnapInput, SnapSelectMenu } from "suit";
import { selectedBudgetItem } from "types/budgets";

type Step4AddBudgetItemProps = {
  selectedSeasonId: string;
  selectedTransaction?: SpendTransactionFix;
  toggleAddBudgetItem: () => void;
};

function Step4AddBudgetItem({
  selectedSeasonId,
  selectedTransaction,
  toggleAddBudgetItem,
}: Step4AddBudgetItemProps) {
  const { toast } = useAppContexts();

  const [amount, setAmount] = useState("");
  const [selectedCategoryId, setSelectedCategory] = useState<string>();
  const [selectedCategoryType, setselectedCategoryType] = useState<string>();
  const [categories, setCategories] = useState<SpendCategory[]>();
  const [budgetData, setBudgetData] = useState<selectedBudgetItem>({
    budgetId: undefined,
    catId: undefined,
    category: "",
    budgetItem: "",
    date: "",
    amountApplied: "",
  });

  const [getCategory, { data: categoryData, loading: loadingCategory }] =
    useSpendCategoriesLazyQuery();
  const [addNewBudgetItem, { data: addBudgetData, loading: loadingAddBudget }] =
    useSpendBudgetCreateMutation();

  useEffect(() => {
    if (!loadingAddBudget && addBudgetData?.spendBudgetCreate) {
      toast?.setToast({
        message: "Budget item added succesfully",
        type: "success",
      });
      handleCancelResetValues();
    }

    // eslint-disable-next-line
  }, [loadingAddBudget, addBudgetData]);

  useEffect(() => {
    if (selectedCategoryType) {
      getCategory({
        variables: {
          filterBy: CategoryFilterEnum.Type,
          filterValue: selectedCategoryType.toLowerCase(),
        },
        fetchPolicy: "network-only",
      });
    }
    // eslint-disable-next-line
  }, [selectedCategoryType]);

  useEffect(() => {
    if (!loadingCategory && categoryData?.spendCategories) {
      setCategories(categoryData.spendCategories.categories as SpendCategory[]);
    }
  }, [loadingCategory, categoryData]);

  const handleAddedBudgetItem = () => {
    if (!selectedCategoryId) {
      toast?.setToast({
        message: "Please select a category",
        type: "danger",
      });
    } else if (isNullOrEmpty(budgetData.budgetItem)) {
      toast?.setToast({
        message: "Description can not be empty",
        type: "danger",
      });
    } else {
      addNewBudgetItem({
        variables: {
          input: {
            categoryId: selectedCategoryId,
            description: budgetData.budgetItem,
            targetAmount: ParseMoney(amount),
            targetDateAt: budgetData.date,
            seasonId: selectedSeasonId,
          },
        },
        refetchQueries: ["SpendGroupById"],
        fetchPolicy: "network-only",
      });
    }
  };

  const handleCancelResetValues = () => {
    setBudgetData({
      budgetId: undefined,
      catId: undefined,
      category: "",
      budgetItem: "",
      date: "",
      amountApplied: "",
    });
    setAmount("");
    setSelectedCategory(undefined);
  };

  return (
    <>
      <p>Previous Budget Item Info</p>
      <p className="font-bold mt-5">
        Description:{" "}
        <span className="font-normal">
          {selectedTransaction?.budgetDescription}
        </span>
      </p>
      <p className="font-bold">
        Amount:{" "}
        <span className="font-normal">
          {FormatMoney(selectedTransaction?.budgetTargetAmount)}
        </span>
      </p>
      <p className="font-bold">
        Date:{" "}
        <span className="font-normal">
          {FormatDate(selectedTransaction?.budgetTargetDate)}
        </span>
      </p>
      <p className="font-bold">
        Category Type:{" "}
        <span className="font-normal capitalize">
          {selectedTransaction?.categoryType}
        </span>
      </p>
      <p className="font-bold">
        Category Name:{" "}
        <span className="font-normal">{selectedTransaction?.categoryName}</span>
      </p>
      <div className="mt-5">
        <p>Select a Category</p>
        <SnapSelectMenu
          selectMenuOptions={[
            {
              name: "Income",
              selected: selectedCategoryType === "Income",
            },
            {
              name: "Expense",
              selected: selectedCategoryType === "Expense",
            },
          ]}
          onSnap-select-menu-updated={(e) => {
            const selectedItem = e.detail.find((s) => s.selected);
            if (selectedItem) {
              setselectedCategoryType(selectedItem.name);
            }
          }}
        />
      </div>
      <div className="lg:grid grid-cols-2">
        <SnapSelectMenu
          label="Category"
          className="lg:mr-6"
          selectMenuOptions={categories?.map((c) => ({
            name: c.name,
            value: c.id!,
            selected: selectedCategoryId === c.id,
          }))}
          onSnap-select-menu-updated={(e) => {
            const selectedItem = e.detail.find((d) => d.selected);
            if (selectedItem) {
              setSelectedCategory(selectedItem.value);
            }
          }}
        />
        <SnapInput
          _id={""}
          label="Budget Item Name"
          className="lg:mt-0 mt-6"
          value={budgetData.budgetItem}
          onSnap-input-change={(e) => {
            budgetData.budgetItem = e.detail.target.value;
          }}
        />
        <div className="mt-6 mr-4">
          <InputMask
            amount={amount}
            setAmount={setAmount}
            hasError={false}
            onChange={() => {}}
          />
        </div>
        <DatePicker
          className="mt-6"
          label={"Date"}
          date={getDatePickerValue(budgetData.date ?? "")}
          setDate={(e) => {
            const newDate = setDatePickerValue(e);
            setBudgetData({
              ...budgetData,
              date: newDate,
            });
          }}
        />
      </div>
      <div className="flex mt-5">
        <SnapButton
          variant="tertiary"
          onClick={toggleAddBudgetItem}
          className="mr-4"
        >
          Back
        </SnapButton>
        <SnapButton
          variant="primary"
          onClick={handleAddedBudgetItem}
          disabled={loadingAddBudget}
        >
          Add Budget Item
        </SnapButton>
      </div>
    </>
  );
}

export default Step4AddBudgetItem;
